import { SYNTHETIC_TRANSPORT_ERROR_CODE } from '../state/constants';
import { readSseChunk } from './readSseChunk';
import { fetchWithSidekickAuth, SidekickHttpError } from './sidekickHttp';

export const SIDEKICK_SSE_READINESS_MAX_EMPTY_CHUNKS = 5;

/** Params accepted by {@link subscribe}. */
export type SubscribeParams = {
  /**
   * Host-supplied Sidekick API base URL (e.g.
   * `${globalThis.appConfig.apiBaseUrl}/sidekick` in `mf-insights`).
   * Transport does not prepend an API version or service segment.
   */
  apiBaseUrl: string;
  conversationId: string;
  /**
   * Resume cursor for mid-turn reconnect (`lastEventId` query param).
   * Omitted or empty on the first subscribe of a turn.
   */
  lastEventId?: string;
  signal: AbortSignal;
};

/**
 * Result of a successful SSE subscribe: the response body reader for
 * {@link pumpSseReader} and the raw `Response` (status, headers).
 */
export type SubscribeResult = {
  reader: ReadableStreamDefaultReader<Uint8Array>;
  /**
   * First chunk read from the stream to prove the SSE connection is
   * established before the client publishes the user message. Passed
   * into `pumpSseReader` so readiness bytes are not dropped.
   */
  initialChunk: Uint8Array;
  response: Response;
};

const buildEventsUrl = (
  apiBaseUrl: string,
  conversationId: string,
  lastEventId?: string
): string => {
  const base = apiBaseUrl.replace(/\/$/, '');
  const params = new URLSearchParams();
  params.set('lastEventId', lastEventId ?? '');
  params.set('includeHistory', '0');
  return `${base}/conversations/${encodeURIComponent(conversationId)}/events?${params.toString()}`;
};

/**
 * Opens the per-turn SSE subscription:
 * `GET {apiBaseUrl}/conversations/{id}/events?lastEventId=...&includeHistory=0`
 * with `Accept: text/event-stream` and JWT Bearer auth (401 refresh once,
 * bounded single 409 retry). Reads one chunk before returning so the
 * caller only publishes after the event stream is actually open.
 */
export const subscribe = async (
  params: SubscribeParams
): Promise<SubscribeResult> => {
  const { apiBaseUrl, conversationId, lastEventId, signal } = params;
  const url = buildEventsUrl(apiBaseUrl, conversationId, lastEventId);

  const response = await fetchWithSidekickAuth({
    url,
    signal,
    init: {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
    },
  });

  const body = response.body;
  if (body === null) {
    throw new Error('Sidekick SSE: response body is null');
  }

  const reader = body.getReader();
  let initialChunk: Uint8Array | undefined;
  let emptyReadCount = 0;
  while (initialChunk === undefined) {
    const { value, done } = await readSseChunk({
      reader,
      signal,
      timeoutMessage: 'Sidekick SSE: stream readiness timed out',
    });
    if (done || value === undefined) {
      await reader.cancel().catch(() => undefined);
      throw new Error('Sidekick SSE: stream closed before ready');
    }
    if (value.byteLength > 0) {
      initialChunk = value;
      continue;
    }
    emptyReadCount += 1;
    if (emptyReadCount > SIDEKICK_SSE_READINESS_MAX_EMPTY_CHUNKS) {
      await reader.cancel().catch(() => undefined);
      throw new SidekickHttpError(0, {
        code: SYNTHETIC_TRANSPORT_ERROR_CODE,
        message: 'Sidekick SSE: stream readiness exceeded empty read budget',
      });
    }
  }

  return { reader, initialChunk, response };
};

/** Re-export for tests that assert backoff timing. */
export { SIDEKICK_HTTP_CONFLICT_BACKOFF_MS as SSE_ALREADY_CONNECTED_BACKOFF_MS } from './sidekickHttp';
